import { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import './CardsSlider.scss';
import { AddNewItemModal, SlideAdd, SlideItem, Spinner } from '@shared/widgets';
import { useGetCardsStore } from '@lib/store';
import { AddItemEnum } from '@shared/api/rest/models';
import { CardSlideItem } from './widgets';
import { TransferToCardModal } from '@shared/widgets/payments/widgets';
import { GetCardsResponse } from '@shared/api/rest/misc';

const settings = {
  dots: false,
  arrows: true,
  infinite: false,
  speed: 800,
  slidesToShow: 3,
  slidesToScroll: 1,
  variableWidth: false,
  adaptiveHeight: true,
  cssEase: 'ease-out',
  responsive: [
    {
      breakpoint: 768,
      settings: {
        dots: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: '0',
        variableWidth: false,
        adaptiveHeight: true,
      },
    },
  ],
};

interface CardsSliderProps {
  animate?: boolean;
  isVisibleDownloadButton?: boolean;
}

export const CardsSlider = (props: CardsSliderProps) => {
  const cardStore = useGetCardsStore();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
  const [slideWidth, setSlideWidth] = useState<number>(390);
  const [isVisibleTransferToCardModal, setIsVisibleTransferToCardModal] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<GetCardsResponse>();

  const sliderWrapRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<any | null>(null);
  const cardImgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    cardStore.get();
  }, []);

  useEffect(() => {
    const updateDimensions = () => {
      if (!sliderWrapRef.current || !sliderRef.current) return;

      const currentSettings = sliderRef.current.innerSlider?.props;
      const currentSlidesToShow = currentSettings.slidesToShow;

      setIsMobile(currentSlidesToShow === 1);

      const sliderWidth = sliderWrapRef.current.clientWidth;
      setSlideWidth(sliderWidth / currentSlidesToShow);
    };

    const timer = setTimeout(updateDimensions, 50);

    window.addEventListener('resize', updateDimensions);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const width = isMobile ? slideWidth : slideWidth + (window.innerWidth > 1024 ? 22 : 14);
  const height = isMobile ? 300 : slideWidth > 350 ? 350 : slideWidth;

  return (
    <div ref={sliderWrapRef} className='mt-10'>
      {!cardStore.success ? (
        <div style={{ height: height }} className='mx-auto flex items-center'>
          <Spinner />
        </div>
      ) : (
        <Slider ref={sliderRef} className='sliderAnimation slider-cards cursor-pointer text-center' {...settings}>
          {cardStore.data.map((card) => (
            <SlideItem
              key={card.Id}
              card={card}
              setSelectedCard={setSelectedCard}
              className={`min-h-[353px] max-w-[400px] px-2`}
              style={{ width: width, height: height }}
            >
              <CardSlideItem
                cardImgRef={cardImgRef}
                card={card}
                selectedCard={selectedCard}
                setIsVisibleTransferToCardModal={setIsVisibleTransferToCardModal}
                setSelectedCard={setSelectedCard}
                isVisibleDownloadButton={props.isVisibleDownloadButton}
              />
            </SlideItem>
          ))}
          <SlideAdd text='Add card' className={`mb-[19px] max-w-[400px]`} onClick={setIsVisibleModal} style={{ width: width, height: height }} />
        </Slider>
      )}

      <AddNewItemModal isOpen={isVisibleModal} onClose={setIsVisibleModal} type={AddItemEnum.Card} />
      <TransferToCardModal isOpen={isVisibleTransferToCardModal} onClose={() => setIsVisibleTransferToCardModal(false)} selectedCard={selectedCard} />
    </div>
  );
};
